// packages/core/src/learning/schools/debt-free-ramsey.ts
// ─────────────────────────────────────────────────────────────────────────
// SchoolExhibitEntry — authored to match boglehead-indexing.ts (gold standard).
// ID-bound fields (figures / featuredLessonIds / debateIds) left [] for the
// post-authoring cross-reference pass. (Worldview named no FigureId; Dave
// Ramsey appears as a canon author only.)
// ─────────────────────────────────────────────────────────────────────────

import type { SchoolExhibitEntry } from '../SchoolExhibitMeta';

export const DEBT_FREE_RAMSEY_EXHIBIT: SchoolExhibitEntry = {
	id: 'debt-free-ramsey',
	tagline:
		'Debt free Ramsey treats debt like a behavior emergency, not just a math problem.',
	worldviewSummary:
		'Debt free Ramsey is paying attention to the emotional weight of owing people money. This lens protects you from treating debt as harmless just because the monthly payment looks small. It accepts a simple tradeoff: less optimization, more urgency, less mental noise. The blind spot is that it can underweight credit strategy, interest math nuance, and situations where flexibility matters. At its best, this worldview gives overwhelmed people a clear first mission: stop the bleeding, build control, and get your life back.',
	coreBeliefs: [
		'Debt free Ramsey believes debt is often a behavior problem before it is a math problem.',
		'Debt free Ramsey believes simple rules help people move when they feel stuck.',
		'Debt free Ramsey believes momentum matters because motivation dies when the plan feels too abstract.',
		'Debt free Ramsey believes monthly payments quietly steal future options.',
		'Debt free Ramsey believes financial peace is worth more than squeezing every possible point of optimization.',
		'Debt free Ramsey believes a clear order of operations beats constantly debating the perfect strategy.',
	],
	warnsAgainst: [
		'Normalizing debt because the payment fits this month.',
		'Using credit as a lifestyle bridge instead of facing the real cash flow problem.',
		'Waiting for the mathematically perfect plan while the balance keeps draining your attention.',
		'Confusing access to borrowed money with actual stability.',
	],
	helps: 'This lens helps people who feel overwhelmed, scattered, or tired of debt following them around. It may not fit users who need a more nuanced credit strategy, who are managing complex obligations, or who value optimization more than simplicity.',
	figures: [],
	canon: [
		{
			title: 'The Total Money Makeover',
			author: 'Dave Ramsey',
			oneLineIdea:
				'The core message is that debt freedom starts with behavior, urgency, and a plan simple enough to follow under stress.',
		},
		{
			title: 'Baby Steps',
			oneLineIdea:
				'A staged order of operations turns a messy money life into one next move at a time.',
		},
		{
			title: 'Debt snowball',
			oneLineIdea:
				'Paying small balances first can create emotional momentum, even when another order may look better on a spreadsheet.',
		},
		{
			title: 'Financial Peace University',
			author: 'Dave Ramsey',
			oneLineIdea:
				'Money control is taught as household discipline, shared language, and repeated behavior, not just financial knowledge.',
		},
		{
			title: 'Envelope budgeting',
			oneLineIdea:
				'Giving money a job before it disappears helps make spending visible and concrete.',
		},
	],
	featuredLessonIds: [],
	nativeModuleId: 'school-debt-free-ramsey',
	debateIds: [],
	applyActions: [
		{
			label: 'Check how much debt is shaping your current money picture',
			action: {
				type: 'openDashboard',
				label: 'Review your money picture',
			},
		},
		{
			label: 'Test how faster payoff changes your future options',
			action: {
				type: 'openSimulator',
				label: 'Simulate a payoff plan',
				scenarioType: 'custom',
			},
		},
		{
			label: 'See what your savings rate looks like after debt pressure drops',
			action: {
				type: 'analyzeSavingsRate',
				label: 'Check your savings rate',
			},
		},
		{
			label: 'Write the payment you are most tired of carrying',
			action: {
				type: 'noToolAction',
				label: 'Name the payment',
				prompt: 'Write the one payment you would most want gone and what would change in your life if it disappeared.',
			},
		},
	],
	marketingHooks: [
		{
			line: 'Debt free Ramsey notices the payment everyone ignores until it owns their calendar.',
			kind: 'hook',
		},
		{
			line: 'What debt payment felt normal at first, then started controlling your choices?',
			kind: 'interview-question',
		},
		{
			line: 'Nisk lets you explore why simple money rules sometimes beat complicated optimization.',
			kind: 'kickstarter-beat',
		},
		{
			line: 'Ask someone what changed emotionally when one monthly payment finally disappeared.',
			kind: 'creator-prompt',
		},
		{
			line: 'The hidden cost of debt is not only interest. It is attention, stress, and fewer options.',
			kind: 'hook',
		},
		{
			line: 'Show two people with the same income, one with payments everywhere and one with breathing room.',
			kind: 'creator-prompt',
		},
		{
			line: 'Where does the debt free mindset help people move, and where can it get too rigid?',
			kind: 'interview-question',
		},
		{
			line: 'Debt freedom is not about moral purity. It is about buying back control.',
			kind: 'hook',
		},
	],
	revision: 1,
};
